import './charts.js';
/** Account appearance: server state only; draft preview never publishes a cookie or changes other apps. */
(() => {
    'use strict';
    const meta = document.querySelector('meta[name="skm-appearance"]');
    if (!meta || window.skmAppearance) return;
    let config;
    try { config = JSON.parse(meta.content); } catch (_) { return; }
    let state = {...config};
    const initialUser = state.userId;
    const root = document.documentElement;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const clone = value => JSON.parse(JSON.stringify(value));
    const keys = ['theme','darkTone','background','accent','font','size','weight','lineHeight','density','contrast','table','motion'];
    const attribute = key => 'data-appearance-' + key.replace(/[A-Z]/g, c => '-' + c.toLowerCase());
    const cookieName = 'skm_appearance_revision';
    let pending = null, retryAt = 0;
    const theme = () => state.preferences.theme === 'dark' || state.preferences.theme === 'system' && media.matches ? 'dark' : 'light';
    const signal = () => (document.cookie.split('; ').find(row => row.startsWith(cookieName + '=')) || '').slice(cookieName.length + 1);
    let seenSignal = signal();
    let printing = false;
    function logos(scope = document) {
        const dark = !printing && state.applied && theme() === 'dark';
        scope.querySelectorAll('[data-appearance-logo]').forEach(img => {
            const next = dark && img.dataset.darkSrc ? img.dataset.darkSrc : img.dataset.lightSrc;
            if (next && img.getAttribute('src') !== next) img.setAttribute('src', next);
        });
    }
    function render() {
        if (state.applied) {
            root.dataset.appearance = 'active';
            keys.forEach(key => root.setAttribute(attribute(key === 'theme' ? 'mode' : key), String(state.preferences[key])));
            root.dataset.appearanceTheme = theme();
            // Existing editor/workspace observers receive a display change, never a remount.
            if (document.body?.classList.contains('skm-wiki-body')) {
                document.body.classList.remove('skm-wiki-theme-light','skm-wiki-theme-dark','skm-wiki-theme-system');
                document.body.classList.add('skm-wiki-theme-' + theme());
            }
            if (root.hasAttribute('data-chat-theme')) root.dataset.chatTheme = theme();
            // Only legacy global zoom markings are cleared; specialized page zoom remains intact.
            document.querySelectorAll('[data-appearance-legacy-font]').forEach(el => {
                el.style.removeProperty('font-size'); el.removeAttribute('data-appearance-legacy-font');
            });
        } else {
            root.removeAttribute('data-appearance');
            keys.forEach(key => root.removeAttribute(attribute(key)));
            root.removeAttribute('data-appearance-mode');
        }
        logos();
        document.dispatchEvent(new CustomEvent('skm:appearance-changed', {detail:{...clone(state), resolvedTheme:theme()}}));
    }
    function apply(next) {
        if (!next || !next.preferences || !Number.isInteger(next.revision)) return;
        if (!config.preview && next.userId === state.userId && next.revision < state.revision) return;
        state = {...config, ...clone(next)};
        render();
    }
    function publish() {
        if (config.preview || !state.userId) return;
        const domain = config.cookieDomain;
        const hostname = location.hostname;
        const bare = String(domain || '').replace(/^\./, '');
        const shared = bare && (hostname === bare || hostname.endsWith('.' + bare));
        // Non-sensitive version hint only. The receiving app always reads its own authenticated API.
        const value = state.userId + ':' + state.revision;
        document.cookie = cookieName + '=' + value + '; Path=/; SameSite=Lax; Max-Age=31536000' + (shared ? '; Domain=' + domain : '') + (location.protocol === 'https:' ? '; Secure' : '');
        seenSignal = signal();
    }
    async function request(method, data) {
        const url = method === 'GET' ? config.readUrl : config.updateUrl;
        if (new URL(url, location.href).origin !== location.origin) throw new Error('설정 화면에서 다시 적용해 주세요.');
        // Common helper when supplied by the shell; independent shells use the same session+CSRF contract.
        if (typeof window.skmSessionApiRequest === 'function') {
            return new Promise((resolve, reject) => window.skmSessionApiRequest({
                url, type:method, data: data ? JSON.stringify(data) : undefined,
                contentType:'application/json', dataType:'json', timeout:10000, skmSilent:true,
                success: result => resolve(result.data),
                error: xhr => reject(Object.assign(new Error('화면 설정을 저장하지 못했습니다.'), {status:xhr.status, response:xhr.responseJSON, retryAfter:xhr.getResponseHeader('Retry-After')})),
            }));
        }
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 10000);
        try {
            const response = await fetch(url, {method, credentials:'same-origin', cache:'no-store', signal:controller.signal,
                headers:{'Accept':'application/json','X-Requested-With':'XMLHttpRequest','Content-Type':'application/json','X-CSRF-TOKEN':document.querySelector('meta[name="csrf-token"]')?.content || ''},
                ...(data ? {body:JSON.stringify(data)} : {})});
            const body = await response.json();
            if (!response.ok) throw Object.assign(new Error('화면 설정을 저장하지 못했습니다.'), {status:response.status,response:body,retryAfter:response.headers.get('Retry-After')});
            return body.data;
        } finally { clearTimeout(timer); }
    }
    async function refresh() {
        if (config.preview || !initialUser || Date.now() < retryAt) return state;
        if (pending) return pending;
        pending = request('GET').then(next => {
            if (next.userId !== initialUser) {
                apply({...state,userId:null,applied:false});
                config.preview = true; // This old page belongs to a previous login; stop following it.
            } else if (next.revision >= state.revision) apply(next);
            seenSignal = signal();
            return clone(state);
        }).catch(error => {
            retryAt = Date.now() + Math.max(30000, (Number(error.retryAfter) || 0) * 1000);
            if ([401,403,419].includes(error.status)) { apply({...state,applied:false,userId:null}); config.preview = true; }
            throw error; // Display failure keeps the last confirmed state; no reload or draft loss.
        }).finally(() => { pending = null; });
        return pending;
    }
    window.skmAppearance = {isApplied:() => state.applied === true, getState:() => clone(state),getTheme:theme,
        apply,refresh,publish,request,settingsUrl:config.settingsUrl};
    render();
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', render, {once:true});
    media.addEventListener('change', () => { if (state.applied && state.preferences.theme === 'system') render(); });
    // The paper palette is light even when the screen preference is dark.
    window.addEventListener('beforeprint', () => { printing = true; logos(); });
    window.addEventListener('afterprint', () => { printing = false; logos(); });
    if (!config.preview && initialUser) {
        const safelyRefresh = () => { if (!document.hidden) refresh().catch(() => {}); };
        window.addEventListener('focus', safelyRefresh);
        window.addEventListener('pageshow', safelyRefresh);
        document.addEventListener('visibilitychange', safelyRefresh);
        window.addEventListener('online', safelyRefresh);
        setInterval(() => { if (!document.hidden && signal() !== seenSignal) safelyRefresh(); }, 2000);
    }
    // Dynamically inserted logos only; never mutate editors, form values, or uploaded images.
    new MutationObserver(records => { if (records.some(r => r.addedNodes.length)) logos(); }).observe(root, {childList:true,subtree:true});
})();
